/**
 * PushNotificationPrompt - Demande simple d'activation des notifications
 * Affiche un prompt avant le paiement pour activer les notifications
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellRing, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Convertir la clé VAPID base64 en Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushNotificationPrompt({ onComplete, compact = false }) {
  const { token } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    const check = async () => {
      const supported = 'serviceWorker' in navigator && 
                        'PushManager' in window && 
                        'Notification' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
        } catch {
        }
      }
    };
    
    check();
  }, []);

  const subscribe = useCallback(async () => {
    if (!isSupported || !token) return;
    
    setIsLoading(true);
    
    try {
      // Demander permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        toast.error('Permission refusée');
        setIsLoading(false);
        return;
      }
      
      // Récupérer clé VAPID
      const vapidRes = await axios.get(`${API}/ftpe-adhesion/push/vapid-key`);
      const vapidKey = vapidRes.data.publicKey;
      
      if (!vapidKey) {
        throw new Error('Clé VAPID non disponible');
      }
      
      // Enregistrer SW
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw-push.js');
        await navigator.serviceWorker.ready;
      }
      
      // S'abonner
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
      
      // Envoyer au backend
      await axios.post(
        `${API}/ftpe-adhesion/push/subscribe`,
        { subscription: subscription.toJSON() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIsSubscribed(true);
      toast.success('🔔 Notifications activées ! Vous serez averti quand votre paiement sera validé.');
      
      if (onComplete) onComplete(true);
      
    } catch {
      toast.error('Impossible d\'activer les notifications');
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, token, onComplete]);

  const dismiss = () => {
    setIsDismissed(true);
    if (onComplete) onComplete(false);
  };

  // Ne pas afficher si pas supporté, déjà inscrit, ou fermé
  if (!isSupported || isSubscribed || isDismissed || permission === 'denied') {
    return null;
  }

  // Version compacte (juste un bouton)
  if (compact) {
    return (
      <Button
        onClick={subscribe}
        disabled={isLoading}
        variant="outline"
        size="sm"
        className="text-amber-600 border-amber-300 hover:bg-amber-50"
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Bell className="w-4 h-4 mr-2" />
        )}
        Activer les notifications
      </Button>
    );
  }

  // Version complète (card avec explication)
  return (
    <div className="relative p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 mb-6">
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
      >
        <X className="w-4 h-4" />
      </button>
      
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
          <BellRing className="w-6 h-6 text-amber-600" />
        </div>
        
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-1">
            Recevoir une notification de confirmation
          </h4>
          <p className="text-sm text-gray-600 mb-3">
            Activez les notifications pour être averti instantanément quand votre paiement sera validé, 
            même si vous quittez cette page.
          </p>
          
          <div className="flex gap-2">
            <Button
              onClick={subscribe}
              disabled={isLoading}
              size="sm"
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Bell className="w-4 h-4 mr-2" />
              )}
              Activer
            </Button>
            <Button
              onClick={dismiss}
              variant="ghost"
              size="sm"
              className="text-gray-500"
            >
              Plus tard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
